import { create } from 'zustand';
import type { BulkSelection } from '@/lib/email-accounts';

/**
 * Transient hand-off store for the Bulk Warmup Settings page.
 *
 * The accounts-table `BulkActionBar` stashes the current `selection` envelope
 * (plus the selected count) here, then navigates to
 * `/email-accounts/bulk-warmup-settings`. The page reads `pending` to know which
 * accounts the patch will apply to; Cancel/Apply `clear()` it.
 *
 * Intentionally NOT persisted: a full page reload loses it, so the page guards
 * on `pending === null` and redirects back to `/email-accounts` (a deep-link or
 * reload has no selection to operate on). This mirrors the ephemeral, Redis-only
 * precedent of `bulk-job-store` — selection state never outlives the navigation.
 */
export type PendingBulkWarmup = {
  /** The resolved API selection envelope (`ids` or `filter` + `excludeIds`). */
  selection: BulkSelection;
  /** Total accounts the patch will be applied to. */
  count: number;
  /** Of `count`, how many are currently running — `null` when not cheaply
   * knowable (e.g. `filter` select-all mode). MVP shows only `count`. */
  runningCount: number | null;
  /** Of `count`, how many are currently stopped — `null` when not knowable. */
  stoppedCount: number | null;
};

export interface BulkWarmupSettingsState {
  pending: PendingBulkWarmup | null;
  setPending: (pending: PendingBulkWarmup) => void;
  clear: () => void;
}

export const useBulkWarmupSettingsStore = create<BulkWarmupSettingsState>()((set) => ({
  pending: null,
  setPending: (pending) => set({ pending }),
  clear: () => set({ pending: null }),
}));
